import React from 'react';

import { Box, Typography } from '@mui/material';

import { palette } from '@app/theme';

import type { DueDiligenceSectionProperties } from './DueDiligenceSection.types';
import type { DueDiligence } from '@api/aiReviews/api.aiReviews.reviewTypes';



type QuestionGroupConfig = {
  key: keyof DueDiligence;
  title: string;
  accentColor: string;
};

/**
 * Ordered config for Due Diligence question categories.
 * Add new categories here — the component renders automatically.
 */
const QUESTION_GROUPS: QuestionGroupConfig[] = [
  { key: 'critical_questions', title: 'Критические вопросы', accentColor: palette.error.main },
  { key: 'founder_questions', title: 'Вопросы основателям', accentColor: '#7B1FA2' },
  { key: 'financial_questions', title: 'Финансовые вопросы', accentColor: palette.warning.main },
  { key: 'product_questions', title: 'Вопросы по продукту', accentColor: palette.primary.main },
  { key: 'market_questions', title: 'Вопросы по рынку', accentColor: palette.primary.main },
  { key: 'sales_questions', title: 'Вопросы по продажам', accentColor: palette.success.main },
  { key: 'legal_questions', title: 'Юридические вопросы', accentColor: '#5D4037' },
  { key: 'exit_questions', title: 'Вопросы по выходу', accentColor: '#455A64' },
];

type QuestionGroupProperties = {
  title: string;
  questions: string[];
  accentColor: string;
  dataTest: string;
};

const QuestionGroup: React.FC<QuestionGroupProperties> = ({
  title,
  questions,
  accentColor,
  dataTest,
}) => {
  return (
    <Box data-testid={dataTest}>
      <Box
        sx={{
          pl: 2,
          borderLeft: `4px solid ${accentColor}`,
          mb: 1.5,
        }}
      >
        <Typography
          data-testid={`${dataTest}_title`}
          sx={{
            fontSize: '0.9375rem',
            fontWeight: 700,
            color: 'text.primary',
            lineHeight: 1.4,
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box
        component='ol'
        data-testid={`${dataTest}_list`}
        sx={{
          m: 0,
          pl: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        {questions.map((question, index) => (
          <Box
            component='li'
            key={index}
            data-testid={`${dataTest}_item-${index}`}
            sx={{
              fontSize: '0.875rem',
              color: 'text.secondary',
              lineHeight: 1.6,
              pl: 0.5,
            }}
          >
            <Typography
              component='span'
              sx={{
                fontSize: '0.875rem',
                color: 'text.secondary',
                lineHeight: 1.6,
              }}
            >
              {question}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const DueDiligenceSection: React.FC<DueDiligenceSectionProperties> = ({
  dueDiligence,
  dataTest = 'due-diligence-section',
}) => {
  const groups = QUESTION_GROUPS
    .map((config) => ({
      ...config,
      questions: dueDiligence[config.key] ?? [],
    }))
    .filter((group) => group.questions.length > 0);

  if (groups.length === 0) {
    return;
  }

  return (
    <Box
      data-testid={dataTest}
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      {groups.map((group) => (
        <QuestionGroup
          key={group.key}
          title={group.title}
          questions={group.questions}
          accentColor={group.accentColor}
          dataTest={`${dataTest}_${group.key.replace('_questions', '')}`}
        />
      ))}
    </Box>
  );
};
